import React, { useState, useEffect } from 'react';
import {
  ThemeProvider,
  createTheme,
  CssBaseline,
  Box,
  AppBar,
  Toolbar,
  Typography,
  Container,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  IconButton,
  Alert,
  Snackbar,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Devices as DevicesIcon,
  Settings as SettingsIcon,
  Menu as MenuIcon,
  Brightness4 as DarkModeIcon,
  Brightness7 as LightModeIcon,
} from '@mui/icons-material';

import Dashboard from './components/Dashboard';
import DeviceManagement from './components/DeviceManagement';
import ConfigurationPanel from './components/ConfigurationPanel';
import { configAPI } from './services/api';

const drawerWidth = 240;

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
});

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#90caf9',
    },
    secondary: {
      main: '#f48fb1',
    },
  },
});

function App() {
  const [darkMode, setDarkMode] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [currentView, setCurrentView] = useState('dashboard');
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });
  const [healthStatus, setHealthStatus] = useState(null);

  return (
    <ThemeProvider theme={darkMode ? darkTheme : theme}>
      <AppContent
        darkMode={darkMode}
        setDarkMode={setDarkMode}
        drawerOpen={drawerOpen}
        setDrawerOpen={setDrawerOpen}
        currentView={currentView}
        setCurrentView={setCurrentView}
        notification={notification}
        setNotification={setNotification}
        healthStatus={healthStatus}
        setHealthStatus={setHealthStatus}
      />
    </ThemeProvider>
  );
}

function AppContent({
  darkMode,
  setDarkMode,
  drawerOpen,
  setDrawerOpen,
  currentView,
  setCurrentView,
  notification,
  setNotification,
  healthStatus,
  setHealthStatus,
}) {
  const currentTheme = useTheme();
  const isMobile = useMediaQuery(currentTheme.breakpoints.down('md'));

  useEffect(() => {
    // Check API health on mount
    checkHealth();
    const interval = setInterval(checkHealth, 30000); // Check every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const checkHealth = async () => {
    try {
      const health = await configAPI.healthCheck();
      setHealthStatus(health);
    } catch (error) {
      console.error('Health check failed:', error);
      setHealthStatus({ status: 'unhealthy' });
    }
  };

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleThemeToggle = () => {
    setDarkMode(!darkMode);
  };

  const handleViewChange = (view) => {
    setCurrentView(view);
    // Close drawer on mobile after selecting a view
    if (isMobile) {
      setDrawerOpen(false);
    }
  };

  const showNotification = (message, severity = 'info') => {
    setNotification({ open: true, message, severity });
  };

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, view: 'dashboard' },
    { text: 'Devices', icon: <DevicesIcon />, view: 'devices' },
    { text: 'Configuration', icon: <SettingsIcon />, view: 'configuration' },
  ];

  const renderView = () => {
    switch (currentView) {
      case 'dashboard':
        return <Dashboard showNotification={showNotification} />;
      case 'devices':
        return <DeviceManagement showNotification={showNotification} />;
      case 'configuration':
        return <ConfigurationPanel showNotification={showNotification} />;
      default:
        return <Dashboard showNotification={showNotification} />;
    }
  };

  return (
    <>
      <CssBaseline />
      <Box sx={{ display: 'flex' }}>
        {/* App Bar */}
        <AppBar
          position="fixed"
          sx={{
            zIndex: (theme) => theme.zIndex.drawer + 1,
          }}
        >
          <Toolbar>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
            <Typography 
              variant="h6" 
              noWrap 
              component="div" 
              sx={{ 
                flexGrow: 1,
                fontSize: { xs: '1rem', sm: '1.25rem' } // Smaller on mobile
              }}
            >
              {isMobile ? 'DGT7 Simulator' : 'DGT7 Device Simulator'}
            </Typography>
            {healthStatus && (
              <Typography 
                variant="body2" 
                sx={{ 
                  mr: 2,
                  display: { xs: 'none', sm: 'block' } // Hide on mobile
                }}
              >
                Status: {healthStatus.status === 'healthy' ? '✓ Online' : '✗ Offline'}
              </Typography>
            )}
            <IconButton color="inherit" onClick={handleThemeToggle}>
              {darkMode ? <LightModeIcon /> : <DarkModeIcon />}
            </IconButton>
          </Toolbar>
        </AppBar>

        {/* Drawer */}
        <Drawer
          variant={isMobile ? 'temporary' : 'persistent'}
          open={drawerOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better mobile performance
          }}
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
            },
          }}
        >
          <Toolbar />
          <Box sx={{ overflow: 'auto' }}>
            <List>
              {menuItems.map((item) => (
                <ListItem key={item.text} disablePadding>
                  <ListItemButton
                    selected={currentView === item.view}
                    onClick={() => handleViewChange(item.view)}
                  >
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.text} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Box>
        </Drawer>

        {/* Main Content */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: { xs: 1, sm: 2, md: 3 }, // Responsive padding
            marginLeft: isMobile ? 0 : (drawerOpen ? 0 : `-${drawerWidth}px`),
            transition: isMobile ? 'none' : 'margin 0.3s',
            width: { xs: '100%', md: 'auto' },
          }}
        >
          <Toolbar />
          <Container maxWidth="xl" sx={{ px: { xs: 0, sm: 2 } }}>
            {renderView()}
          </Container>
        </Box>

        {/* Notification Snackbar */}
        <Snackbar
          open={notification.open}
          autoHideDuration={6000}
          onClose={handleCloseNotification}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={handleCloseNotification}
            severity={notification.severity}
            sx={{ width: '100%' }}
          >
            {notification.message}
          </Alert>
        </Snackbar>
      </Box>
    </>
  );
}

export default App;


